import { useEffect, useState } from "react";
import { FileSignature, History, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DateField } from "@/components/DateField";
import { Panel } from "@/components/KpiCard";
import { api, errMsg, money } from "@/lib/api";
import { estLongue, typeLocation } from "@/lib/locations";
import { AvisRenouvellementDialog } from "@/components/AvisRenouvellementDialog";
import { LIB_REPONSE } from "@/components/ReponseAvis";
import { HistoriqueLoyers } from "@/components/HistoriqueLoyers";

const Champ = ({ label, children }) => <label className="flex flex-col gap-1 text-[10px] font-mono uppercase tracking-wider text-slate-500">{label}{children}</label>;

const joursAvant = (iso) => Math.round((new Date(`${iso}T00:00:00`) - new Date(new Date().toDateString())) / 86400000);

const BadgeBail = ({ l }) => {
  if (!l.bail_fin || l.parti_le) return null;
  const j = joursAvant(l.bail_fin);
  if (j > 90) return null;
  const txt = j < 0 ? `bail échu depuis ${-j} j` : `bail se termine dans ${j} j`;
  return <span className={`ml-2 rounded px-1.5 py-0.5 text-[10px] font-mono ${j <= 30 ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-800"}`} data-testid={`locataire-bail-badge-${l.id}`}>{txt}</span>;
};

const Item = ({ l, onSave, onDelete, onReload }) => {
  const [avis, setAvis] = useState(false);
  const [histo, setHisto] = useState(false);
  const [loyer, setLoyer] = useState(l.loyer_attendu ?? "");
  const [courriel, setCourriel] = useState(l.courriel || "");
  const blurCourriel = () => { if (courriel.trim() !== (l.courriel || "")) onSave(l, { courriel: courriel.trim() || null }); };
  const blurLoyer = () => {
    const n = String(loyer).trim() === "" ? null : Math.abs(Number(String(loyer).replace(",", ".")));
    if (Number.isNaN(n)) return setLoyer(l.loyer_attendu ?? "");
    if (n !== (l.loyer_attendu ?? null)) onSave(l, { loyer_attendu: n });
  };
  return (
    <li className="flex flex-col md:flex-row md:items-end justify-between gap-3 py-3" data-testid={`locataire-${l.id}`}>
      <div className="min-w-0">
        <p className="text-sm font-medium">{l.nom} <span className="font-mono text-xs text-slate-500">· {l.motcle}</span>
          {l.parti_le && <span className="ml-2 rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-mono text-slate-600" data-testid={`locataire-parti-badge-${l.id}`}>parti le {l.parti_le}</span>}<BadgeBail l={l} /></p>
        <p className="text-xs text-slate-500">{typeLocation(l.type)} · {l.logement} · {l.nb_operations} virement(s) · dernier {l.dernier_depot || "—"}</p>
        {l.hausse && <p className="text-xs text-emerald-700" data-testid={`locataire-hausse-${l.id}`}>{l.hausse.decision ? `Loyer fixé par le TAL ${money(l.hausse.decision.loyer)} dès le ${l.hausse.decision.date_effet}` : `Nouveau loyer ${money(l.hausse.nouveau)} dès le ${l.hausse.a_partir_de}`}{l.hausse.envoye_le ? ` · avis envoyé le ${l.hausse.envoye_le}` : ""}{l.hausse.reponse ? ` · ${LIB_REPONSE[l.hausse.reponse].toLowerCase()} (${l.hausse.reponse_le})` : ""}</p>}
      </div>
      <div className="flex flex-wrap items-end gap-2">
        {estLongue(l.type) && <>
          <Champ label="Courriel du locataire">
            <Input type="email" value={courriel} onChange={(e) => setCourriel(e.target.value)} onBlur={blurCourriel} placeholder="locataire@exemple.com" className="h-8 w-56 text-xs bg-white" data-testid={`locataire-courriel-${l.id}`} />
          </Champ>
          <Champ label={`Loyer (habituel ${money(l.loyer_habituel)})`}>
            <Input value={loyer} onChange={(e) => setLoyer(e.target.value)} onBlur={blurLoyer} placeholder="Auto" inputMode="decimal" className="h-8 w-32 text-right font-mono text-xs bg-white" data-testid={`locataire-loyer-${l.id}`} />
          </Champ>
          {l.type === "loyer" && <>
            <Champ label="Début du bail">
              <DateField value={l.bail_debut || ""} onChange={(v) => onSave(l, { bail_debut: v || null })} className="h-8 w-40 text-xs bg-white" data-testid={`locataire-bail-debut-${l.id}`} />
            </Champ>
            <Champ label="Fin du bail">
              <DateField value={l.bail_fin || ""} onChange={(v) => onSave(l, { bail_fin: v || null })} className="h-8 w-40 text-xs bg-white" data-testid={`locataire-bail-fin-${l.id}`} />
            </Champ>
            {l.bail_fin && <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setAvis(true)} data-testid={`locataire-avis-${l.id}`}><FileSignature className="h-3.5 w-3.5 mr-1" />Avis de renouvellement</Button>}
            {avis && <AvisRenouvellementDialog l={l} open={avis} onClose={() => setAvis(false)} onDone={() => { setAvis(false); onReload(); }} />}
            {(l.hausse || l.paliers?.length > 0) && <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setHisto(!histo)} data-testid={`locataire-historique-${l.id}`}><History className="h-3.5 w-3.5 mr-1" />Historique des loyers</Button>}
            {histo && <HistoriqueLoyers l={l} />}
          </>}
          <Champ label="Parti le">
            <DateField value={l.parti_le || ""} onChange={(v) => onSave(l, { parti_le: v || null })} className="h-8 w-40 text-xs bg-white" data-testid={`locataire-parti-${l.id}`} />
          </Champ>
        </>}
        <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:text-red-600" onClick={() => onDelete(l)} data-testid={`locataire-supprimer-${l.id}`}><Trash2 className="h-4 w-4" /></Button>
      </div>
    </li>
  );
};

export const LocatairesPanel = ({ version, onChange }) => {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/locataires").then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, [version]);
  const save = async (l, champs) => {
    try { await api.put(`/locataires/${l.id}`, champs); toast.success("Locataire mis à jour"); load(); onChange?.(); } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (l) => {
    if (!window.confirm(`Oublier « ${l.nom} » ? Ses virements associés automatiquement ne seront plus liés à son logement.`)) return;
    try { await api.delete(`/locataires/${l.id}`); toast.success("Payeur oublié"); load(); onChange?.(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Panel title="Locataires mémorisés" testId="locataires-panel">
      <p className="text-xs text-slate-500 mb-3">Chaque virement de ces payeurs est associé automatiquement à leur logement. Indiquez la date de départ d'un locataire qui a déménagé : il ne sera plus signalé dans les loyers manquants après ce mois. Pour un bail, indiquez ses dates : un rappel vous est envoyé par courriel 90 jours puis 30 jours avant la fin.</p>
      {rows.length === 0 ? <p className="text-sm text-slate-500">Aucun payeur mémorisé : utilisez « Associer à un logement » sur un dépôt.</p> : (
        <ul className="divide-y divide-slate-100" data-testid="locataires-list">{rows.map((l) => <Item key={`${l.id}-${l.updated_at}`} l={l} onSave={save} onDelete={del} onReload={() => { load(); onChange?.(); }} />)}</ul>
      )}
    </Panel>
  );
};
